import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FirstProcessingUploaderChunk, {
  FirstProcessingParams,
  FirstProcessingUploadProgress,
  FirstProcessingServerResponse
} from '../services/FirstProcessingUploaderChunk';
import { StandardUploadProgress, ChartDataPoint } from '../types/firstProcessing.types';

interface UseFirstProcessingUploadReturn {
  isUploading: boolean;
  uploadProgress: StandardUploadProgress | null;
  uploadError: string | null;
  chartData: ChartDataPoint[];
  uploadFile: (
    fileContent: string,
    params: FirstProcessingParams
  ) => Promise<boolean>;
  resetUpload: () => void;
  retryUpload: () => Promise<void>;
}

export const useFirstProcessingUpload = (): UseFirstProcessingUploadReturn => {
  const { t } = useTranslation('firstProcessing');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<StandardUploadProgress | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  
  // Store last upload params for retry functionality
  const lastUploadParams = useRef<{
    fileContent: string;
    params: FirstProcessingParams;
  } | null>(null);
  
  // Store current upload progress to access uploadId for cancellation
  const uploadProgressRef = useRef<StandardUploadProgress | null>(null);
  
  // Cleanup function for WebSocket
  const cleanupRef = useRef<(() => void) | null>(null);

  // Cleanup on unmount - but only disconnect if not actively uploading
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
      // Only disconnect socket if not actively uploading/processing
      // This prevents killing the connection while backend is still working
      const currentProgress = uploadProgressRef.current;
      if (!currentProgress ||
          currentProgress.status === 'completed' ||
          currentProgress.status === 'error') {
        FirstProcessingUploaderChunk.disconnectSocket();
      } else {
        console.log('📤 useFirstProcessingUpload - Skipping disconnect, upload still in progress');
      }
    };
  }, []);

  const handleProgress = useCallback((
    progress: FirstProcessingUploadProgress,
    fileName: string
  ) => {

    // Convert to standardized format with separate upload/processing progress
    const standardProgress: StandardUploadProgress = {
      fileName: progress.message || fileName || 'CSV File',
      uploadProgress: progress.uploadProgress,        // Frontend chunk upload progress
      processingProgress: progress.processingProgress, // Backend processing progress
      status: progress.status,
      error: progress.error,
      message: progress.message,
      step: progress.step,
      uploadId: progress.uploadId,
      eta: progress.eta,
      etaFormatted: progress.etaFormatted,
      currentStep: progress.currentStep,
      totalSteps: progress.totalSteps
    };

    setUploadProgress(standardProgress);
    uploadProgressRef.current = standardProgress;

    // Update loading state
    setIsUploading(
      progress.status !== 'completed' &&
      progress.status !== 'error'
    );

    // Handle errors
    if (progress.status === 'error') {
      setUploadError(progress.error || t('unknownError'));
    }

    // Clear progress after successful completion
    if (progress.status === 'completed') {
      setTimeout(() => {
        setUploadProgress(null);
      }, 2000);
    }
  }, [t]);

  const uploadFile = useCallback(async (
    fileContent: string,
    params: FirstProcessingParams
  ): Promise<boolean> => {
    // Store params for retry
    lastUploadParams.current = { fileContent, params };

    // Reset state
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(null);
    setChartData([]);

    try {
      // Validate params
      if (!fileContent) {
        throw new Error(t('allFieldsRequired'));
      }

      // Validate numeric values
      if (isNaN(params.tss) || isNaN(params.offset)) {
        throw new Error('TSS and Offset must be valid numbers');
      }

      if (params.tss <= 0) {
        throw new Error('TSS must be greater than 0');
      }

      if (params.offset < 0) {
        throw new Error('Offset must be non-negative');
      }

      // Progress callback
      const onProgress = (progress: FirstProcessingUploadProgress) => {
        handleProgress(progress, 'data.csv');
      };

      // Upload file using chunked uploader
      const result: FirstProcessingServerResponse = await FirstProcessingUploaderChunk.uploadFile(
        fileContent,
        params,
        onProgress
      );

      // Handle result
      if (result.error) {
        throw new Error(result.error);
      } else if (result.success && result.data) {
        // Type guard for result data
        if (Array.isArray(result.data)) {
          setChartData(result.data as ChartDataPoint[]);
        } else {
          // Handle case where data is not an array
          console.warn('Expected array data, received:', typeof result.data);
          setChartData([]);
        }
        setUploadProgress({
          fileName: 'data.csv',
          uploadProgress: 100,
          processingProgress: 100,
          status: 'completed',
          message: t('successProcessed')
        });
        return true;
      } else {
        throw new Error(t('errorProcessing'));
      }
    } catch (error) {
      let errorMsg = t('connectionError');

      if (!window.navigator.onLine) {
        errorMsg = t('noInternet');
      } else if (error instanceof TypeError && error.message === 'Failed to fetch') {
        errorMsg = t('serverNotReachable');
      } else if (error instanceof Error) {
        errorMsg = error.message;
      }

      setUploadError(errorMsg);
      setUploadProgress({
        fileName: 'data.csv',
        uploadProgress: 0,
        processingProgress: 0,
        status: 'error',
        error: errorMsg
      });
      return false;
    } finally {
      setIsUploading(false);
    }
  }, [t, handleProgress]);

  const resetUpload = useCallback(() => {
    setIsUploading(false);
    setUploadProgress(null);
    setUploadError(null);
    setChartData([]);
    lastUploadParams.current = null;
    
    // Cleanup socket connection
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  const retryUpload = useCallback(async () => {
    if (!lastUploadParams.current) {
      setUploadError('No previous upload to retry');
      return;
    }
    
    const { fileContent, params } = lastUploadParams.current;
    await uploadFile(fileContent, params);
  }, [uploadFile]);

  return {
    isUploading,
    uploadProgress,
    uploadError,
    chartData,
    uploadFile,
    resetUpload,
    retryUpload
  };
};